/**
 * AdditionalInformationPanel — native rewrite of the shared AdditionalInformation
 * sub-tab (reason toggle + 3 upload zones + message). Reason/message bind to the
 * form controller; the exterior/nameplate/construction uploads go through the
 * §4b upload sync controller. `OPENS_ADDITIONAL_INFORMATION` is fired by the
 * configurator on panel selection, not from here — the sibling certificate keeps
 * its panels mounted, so a mount effect would report all four at once there.
 */

import React from 'react'
import Stack from '@mui/material/Stack'
import { Trans, useTranslation } from 'react-i18next'

import { EnergyPassReason } from 'constants/purchaseFlow/sharedEnergyCertificateTypes'
import { FileType, certificateDocumentFileTypes } from 'constants/misc'
import { FileAPIUpload } from 'components/common/FileAPIUpload'
import { Label } from 'components/common/Label'
import { MUIInputField } from 'components/common/MUIInputField'
import { SectionedBorderBox } from 'components/common/SectionedBorderBox'
import { ToggleButton } from 'components/common/ToggleButton'
import { SystemMessage } from 'components/common/SystemMessage'

import type { UseEnergyCertificateForm } from '../useEnergyCertificateForm'
import type { useEnergyUploadSync } from '../useEnergyUploadSync'

interface Props {
  form: UseEnergyCertificateForm
  uploads: ReturnType<typeof useEnergyUploadSync>
}

const maximumUploads = 1

export const AdditionalInformationPanel: React.FC<Props> = ({ form, uploads }) => {
  const { t } = useTranslation(['purchase_flow', 'purchase_flow_revamp'], { keyPrefix: 'additional_information_page' })
  // A namespaced key cannot go through the prefixed `t`: i18next would prepend the prefix to the namespace.
  const { t: tRevamp } = useTranslation('purchase_flow_revamp')
  const { view, setReason, setMessage } = form
  const {
    hasRestoredFiles,
    exterior,
    nameplate,
    construction,
    uploadExterior,
    deleteExterior,
    uploadNameplate,
    deleteNameplate,
    uploadConstruction,
    deleteConstruction,
  } = uploads

  return (
    <SectionedBorderBox title={t('title')}>
      <Stack gap="1.6rem" position="relative">

        {hasRestoredFiles &&
          <SystemMessage variant="warning" message={tRevamp('configuration.energy_certificate.session_orphan_warning')} />
        }

        <Stack gap=".6rem">

          <Label text={t('reason_for_issue_certificate')} />

          <Stack flexDirection="row" gap={1} alignItems="center" flexWrap="wrap">
            {Object.values(EnergyPassReason).map((reason) => (
              <ToggleButton
                key={reason}
                sx={{ height: '4rem', justifyContent: 'center' }}
                isSelected={view.reasonForEnergyPass === reason}
                label={t(`energy_certificate_reason.${reason}`)}
                onClick={() => setReason(reason)}
              />
            ))}
          </Stack>

        </Stack>

        <FileAPIUpload
          title={<Trans t={t} i18nKey="exterior_view" values={{ uploadCount: exterior.entries.length, maximumUploads }} />}
          maxNumberOfFiles={1}
          files={exterior.entries}
          acceptedFileTypes={[FileType.JPEG, FileType.PNG]}
          isDraggingFile={false}
          uploadHandler={(acceptedFiles) => uploadExterior(acceptedFiles)}
          onDelete={(fileId) => deleteExterior(fileId)}
          onCancelUpload={(fileId) => deleteExterior(fileId)}
        />

        <FileAPIUpload
          title={<Trans t={t} i18nKey="heater_nameplate" values={{ uploadCount: nameplate.entries.length, maximumUploads }} />}
          maxNumberOfFiles={1}
          files={nameplate.entries}
          acceptedFileTypes={certificateDocumentFileTypes}
          isDraggingFile={false}
          uploadHandler={(acceptedFiles) => uploadNameplate(acceptedFiles)}
          onDelete={(fileId) => deleteNameplate(fileId)}
          onCancelUpload={(fileId) => deleteNameplate(fileId)}
        />

        <FileAPIUpload
          title={<Trans t={t} i18nKey="construction_description" values={{ uploadCount: construction.entries.length, maximumUploads }} />}
          maxNumberOfFiles={1}
          files={construction.entries}
          acceptedFileTypes={certificateDocumentFileTypes}
          isDraggingFile={false}
          uploadHandler={(acceptedFiles) => uploadConstruction(acceptedFiles)}
          onDelete={(fileId) => deleteConstruction(fileId)}
          onCancelUpload={(fileId) => deleteConstruction(fileId)}
        />

        <MUIInputField
          label={t('message_the_expert')}
          placeholder={t('message_the_expert_placeholder')}
          isMultiline
          value={view.messageToExpert}
          onChange={(e) => setMessage(e.target.value)}
        />

      </Stack>
    </SectionedBorderBox>
  )
}
